// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { PowerFlowBlock } from '../../../utils/powerFlowInference';
import type { PowerFlowNode } from '../../../utils/powerFlowLayout';

export interface SldEnclosureLayerProps {
  blocks: PowerFlowBlock[];
  nodesById: Map<string, PowerFlowNode>;
  /** Layout padding, so the frame clears the bus label strip. */
  labelHeight?: number;
}

interface Frame {
  block: PowerFlowBlock;
  x: number;
  y: number;
  w: number;
  h: number;
  /** How many of the block's members are actually drawn inside. */
  drawn: number;
}

const PAD_X = 12;
const PAD_Y = 10;

/**
 * The frame around a block's assets, from the layout's own coordinates.
 *
 * A block has no geometry of its own: it is the bounding box of the chips and
 * bars the layout placed for its members, plus a margin. Members that are not
 * on the canvas — racks behind a count badge, an asset outside this drawing —
 * do not stretch it. A block with fewer than two drawn members has no frame:
 * a dashed box around one chip says nothing.
 */
export const enclosureFrames = (
  blocks: readonly PowerFlowBlock[],
  nodesById: ReadonlyMap<string, PowerFlowNode>,
  labelHeight = 20
): Frame[] => {
  const frames: Frame[] = [];
  for (const block of blocks) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let drawn = 0;
    for (const id of block.memberIds) {
      const node = nodesById.get(id);
      if (!node) continue;
      drawn += 1;
      // A bar's label sits above the bar itself; the frame must clear it.
      const top = node.shape === 'bus' ? node.y - labelHeight : node.y;
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, node.x + node.w);
      maxY = Math.max(maxY, node.y + node.h);
    }
    if (drawn < 2) continue;
    frames.push({
      block,
      x: minX - PAD_X,
      y: minY - PAD_Y - 6,
      w: maxX - minX + PAD_X * 2,
      h: maxY - minY + PAD_Y * 2 + 6,
      drawn,
    });
  }
  return frames;
};

/**
 * Dashed enclosures behind the diagram, one per inferred block.
 *
 * Drawn under the conductor layer and the chips, and non-interactive, so they
 * add grouping without taking a click or a tab stop. The caption names the
 * rule (`from labels`) because the enclosure is a claim: the twin has no block
 * entity, and the drawing says where the grouping came from.
 */
const SldEnclosureLayerComponent = ({ blocks, nodesById, labelHeight }: SldEnclosureLayerProps) => {
  const frames = enclosureFrames(blocks, nodesById, labelHeight);
  if (frames.length === 0) return null;
  return (
    <div className="sld-enclosures" aria-hidden="true">
      {frames.map(frame => (
        <div
          key={frame.block.id}
          className="sld-enclosure"
          style={{ left: frame.x, top: frame.y, width: frame.w, height: frame.h }}
          data-sld-enclosure={frame.block.id}
        >
          <span className="sld-enclosure__label">Block {frame.block.letter} · from labels</span>
        </div>
      ))}
    </div>
  );
};

export const SldEnclosureLayer = React.memo(SldEnclosureLayerComponent);
